import { useState, type FormEvent } from 'react';
import { LIMITS, nicknameSchema } from '@clanfire/shared';
import { serverApi } from '@/api/endpoints';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useSubmit, validate } from '@/hooks/useForm';
import { useAuth } from '@/stores/auth';
import { useServers } from '@/stores/servers';
import { toast } from '@/stores/ui';
import { useUser } from '@/stores/users';

/** Set your own server nickname, or (with Manage Nicknames) someone else's. */
export function NicknameModal({ serverId, userId, onClose }: { serverId: string; userId: string; onClose: () => void }) {
  const user = useUser(userId);
  const isSelf = useAuth((s) => s.user?.id === userId);
  const server = useServers((s) => s.servers[serverId]);
  const current = useServers((s) => (isSelf ? s.servers[serverId]?.me.nickname : s.members[serverId]?.[userId]?.nickname) ?? '');
  const [nickname, setNickname] = useState(current);
  const { loading, error, fields, setFields, run } = useSubmit();

  const save = async (value: string) => {
    const { data, fields: invalid } = validate(nicknameSchema, { nickname: value });
    if (!data) return setFields(invalid);
    const member = await run(() => serverApi.setNickname(serverId, isSelf ? '@me' : userId, data.nickname));
    if (!member) return;
    useServers.getState().upsertMember(member);
    toast.success(member.nickname ? `Nickname set to ${member.nickname}.` : 'Nickname reset.');
    onClose();
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void save(nickname);
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={isSelf ? 'Change nickname' : `Change ${user?.displayName ?? 'member'}'s nickname`}
      description={server ? `Only shown in ${server.name}.` : undefined}
      size="sm"
      footer={
        <>
          {current && (
            <Button variant="ghost" className="mr-auto" disabled={loading} onClick={() => void save('')}>
              Reset nickname
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="nickname-form" loading={loading}>
            Save
          </Button>
        </>
      }
    >
      <form id="nickname-form" onSubmit={submit} className="flex flex-col gap-3">
        <Input
          label="Nickname"
          value={nickname}
          onChange={(e) => setNickname(e.target.value)}
          placeholder={user?.displayName ?? ''}
          maxLength={LIMITS.displayNameMax}
          error={fields.nickname}
          hint="Leave empty to use your display name."
          data-autofocus
        />
        {error && <p className="text-sm text-danger">{error}</p>}
      </form>
    </Modal>
  );
}
